import React from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin, Tooltip } from "antd";
import styled, { css } from "styled-components";
import useThemeContext from "../../../hooks/useThemeContext";
import { TypeColorNames } from "../../../theme/colors";
import { ThemeTypes } from "../../../theme/theme";
import { TypographyTypes } from "../../../theme/typography";
import Typography from "../typography/Typography";
import { getCustomColorFont } from "../../../utils/colorLight";

type ButtonType =
  | "primary"
  | "secondary"
  | "error"
  | "success"
  | "yellow"
  | "white"
  | "blue";
type ButtonVariant = "contained" | "outlined" | "text" | "primary" | "cancel";
type ButtonSize = "small" | "medium" | "large";

interface StyledButtonProps {
  colorType?: ButtonType;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  enable?: boolean;
  theme?: ThemeTypes;
  customColor?: TypeColorNames;
  disabled?: boolean;
  colorFont?: TypeColorNames;
  colorBackground?: TypeColorNames;
  colorHover?: TypeColorNames;
  colorActive?: TypeColorNames;
  colorFocus?: TypeColorNames;
  forceHover?: boolean;
  customBackground?: string;
  type?: "submit" | "reset" | "button";
  rounded?: boolean;
  backgroundActive?: TypeColorNames;
  backgroundHover?: TypeColorNames;
}

export interface IButtonProps {
  colorType?: ButtonType;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  enable?: boolean;
  loading?: boolean;
  style?: any;
  leftIcon?: any;
  rightIcon?: any;
  onClick?: any;
  children?: any;
  tooltip?: String;
  className?: string;
  typographyType?: TypographyTypes;
  withoutTypography?: boolean;
  colorFont?: TypeColorNames;
  colorBackground?: TypeColorNames;
  colorHover?: TypeColorNames;
  colorActive?: TypeColorNames;
  colorFocus?: TypeColorNames;
  forceHover?: boolean;
  color?: string;
  backgroundActive?: TypeColorNames;
  backgroundHover?: TypeColorNames;
  disabled?: boolean;
  customBackground?: string;
  type?: "submit" | "reset" | "button";
  rounded?: boolean;
  customColor?: string;
}

const getBackgroundColor = ({
  colorType,
  variant,
  enable,
  colorBackground,
  theme,
  customBackground,
  disabled
}: StyledButtonProps): string | undefined => {
  if (disabled) return theme?.colors.placeholder;
  if (enable === false || variant === "cancel") return theme?.colors.white;
  if (variant === "outlined" || variant === "text")
  return theme?.colors.transparent;
  if (customBackground) return customBackground;
  if (variant === "primary") return theme?.colors.primary;
  if (colorBackground) return theme?.colors[colorBackground];
  if (!colorType) return theme?.colors.primary;
  return theme?.colors[colorType];
};

const getBorderColor = ({
  colorType,
  variant,
  enable,
  colorBackground,
  customBackground,
  theme,
  disabled
}: StyledButtonProps): string | undefined => {
  if (disabled) return theme?.colors.placeholder;

  if (variant === 'text') return theme?.colors.transparent;
  if (enable === false || variant === "cancel") return theme?.colors.placeholder;
  if (customBackground) return customBackground;
  if (colorBackground) return theme?.colors[colorBackground];
  if (variant === "primary") return theme?.colors.primary;
  if (variant === "outlined" && colorType) return theme?.colors[colorType];
  if (variant === "outlined" && !colorType) return theme?.colors.primary;
  if (variant === "contained" && colorType) return theme?.colors[colorType];
  return theme?.colors.transparent;
};

const getHoverFontColor = ({
  colorType,
  variant,
  enable,
  theme,
  colorHover,
  colorFont,
  customBackground
}: StyledButtonProps): string | undefined => {
  if (customBackground) return getCustomColorFont(customBackground);
  if (enable === false || variant === "cancel") return theme?.colors.placeholder;
  if (colorHover) return theme?.colors[colorHover];
  if (variant === "primary") return theme?.colors.white;
  if (variant === "outlined") {
    switch (colorType) {
      case "primary":
        return theme?.colors.white;
      case "secondary":
        return theme?.colors.white;
      case "success":
        return theme?.colors.white;
      case "error":
        return theme?.colors.white;
      case "yellow":
        return theme?.colors.black;
      case "white":
        return theme?.colors.black;
      default:
        return theme?.colors.primary;
    }
  }
  if (variant === "text") {
    switch (colorType) {
      case "primary":
        return theme?.colors.primaryDark;
      case "secondary":
        return theme?.colors.title;
      case "success":
        return theme?.colors.successDark;
      case "error":
        return theme?.colors.errorDark;
      case "yellow":
        return theme?.colors.black;
      case "white":
        return theme?.colors.white;
      default:
        return theme?.colors.primary;
    }
  }
  if (variant === "contained") {
    if (colorType === "yellow" || colorType === "white")
      return theme?.colors.body;
  }
  return colorFont;
};

const getPressedFontColor = ({
  colorType,
  variant,
  enable,
  theme,
  colorActive,
  colorFont
}: StyledButtonProps): string | undefined => {
  if (enable === false || variant === "cancel") return theme?.colors.placeholder;
  if (colorActive) return theme?.colors[colorActive];
  if (variant === "primary") return theme?.colors.white;
  if ((variant === "outlined" || variant === "text") && colorType === "primary")
    return theme?.colors.primaryDark2;
  if (
    (variant === "outlined" || variant === "text") &&
    colorType === "secondary"
  )
    return theme?.colors.black;
  switch (colorType) {
    case "primary":
      return theme?.colors.white;
    case "secondary":
      return theme?.colors.white;
    default:
      return colorFont;
  }
};

const getColor = ({
  colorType,
  variant,
  enable,
}: StyledButtonProps): TypeColorNames => {
  if (enable === false || variant === "cancel") return "placeholder";
  if ((variant === "outlined" || variant === "text") && colorType)
    return colorType;
  if (
    variant === "contained" &&
    (colorType === "white" || colorType === "yellow")
  )
    return "black";
  if (variant === "outlined" && colorType === "yellow") return "black";
  return "white";
};

const getSize = ({ size, variant }: StyledButtonProps) => {
  const SMALL_SIZE = "8px 24px";
  const MEDIUM_SIZE = "12px 28px";
  const LARGE_SIZE = "16px 36px";
  switch (size) {
    case "small":
      return SMALL_SIZE;
    case "medium":
      return MEDIUM_SIZE;
    case "large":
      return LARGE_SIZE;
    default:
      return MEDIUM_SIZE;
  }
};

const getSizeMobile = ({ size }: StyledButtonProps) => {
  const SMALL_SIZE = "8px 24px";
  const MEDIUM_SIZE = "10px 28px";
  const LARGE_SIZE = "12px 36px";
  switch (size) {
    case "small":
      return SMALL_SIZE;
    case "medium":
      return MEDIUM_SIZE;
    case "large":
      return LARGE_SIZE;
    default:
      return MEDIUM_SIZE;
  }
};

const getHoverBackground = ({
  colorType,
  variant,
  enable,
  theme,
  customBackground,
  backgroundHover,
  colorBackground,
  disabled
}: StyledButtonProps): string | undefined => {
  if (disabled) return theme?.colors.placeholder;

  if (enable === false) return theme?.colors.white;
  if (customBackground) return getCustomColorHover(customBackground)
  if (backgroundHover) return theme?.colors[backgroundHover];
  if (variant === "primary") return theme?.colors.primaryDark;
  if (variant === "cancel") return theme?.colors.inputBackground;
  if (variant === "outlined" || variant === "text")
    return theme?.colors.transparent;
  if (!colorType) return colorBackground;
  return theme?.colors[colorType];
};


const getActiveBackground = ({
  colorType,
  variant,
  enable,
  theme,
  customBackground,
  backgroundActive,
  colorBackground,
}: StyledButtonProps): string | undefined => {
  if (enable === false || variant === "cancel") return theme?.colors.white;
  if (customBackground) return getCustomColorHover(customBackground)
  if (backgroundActive) return theme?.colors[backgroundActive];
  if (variant === "primary") return theme?.colors.primary;
  if (variant === "outlined" || variant === "text")
    return theme?.colors.transparent;
  if (!colorType) return colorBackground;
  return theme?.colors[colorType];
};

//Deixa a cor dada 20% mais escura
const getCustomColorHover = (hex: string) => {

  // Hex to RGB
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);

  const amount = Math.floor(255 * (20 / 100));
  const newR = Math.max(0, r - amount);
  const newG = Math.max(0, g - amount);
  const newB = Math.max(0, b - amount);

  // RGB back to hex
  const newHex = ((newR << 16) | (newG << 8) | newB).toString(16);
  return '#' + ('000000' + newHex).slice(-6);
}

const StyledButton = styled.button<StyledButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${getSize};

  ${(props: StyledButtonProps) => css`
    ${props.theme?.mediaQuerie.maxWidth.md} {
      padding: ${getSizeMobile};
    }
  `}

  color: ${({ customBackground, enable }) => customBackground && getCustomColorFont(customBackground, enable)};

  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
  background-color: ${getBackgroundColor};
  border: 2px solid ${getBorderColor} !important;
  border-radius: ${props => props.rounded || props.variant === "primary" || props.variant === "cancel" ? "40px" : "8px"};
  transition: all 0.5s;
  cursor: ${({ enable }) => (enable ? "pointer" : "not-allowed")};
  text-decoration: none;

  ${({ variant }) => {
    switch (variant) {
      case "text":
        return `
          border: none;
        `;
      default:
        return `
          border: 1px solid ${getBorderColor};
        `;
    }
  }}

  .icon {
    display: flex;
    align-items: center;
  }
  .left-icon {
    margin-right: 12px;
  }
  .right-icon {
    margin-left: 12px;
  }
  .ant-spin {
    color: ${getColor};
    margin-right: 16px;
  }

  &:hover {
    background-color: ${getHoverBackground};
    color: ${getHoverFontColor};

    &.hoverColor {
      span {
        color: ${getHoverFontColor} !important;
      }
    }
  }

  ${(props) =>
    props.forceHover &&
    css`
      background-color: ${props.color};
      color: ${getHoverFontColor};

      .icon {
        path {
          stroke: ${getHoverFontColor};
        }
      }

      &.hoverColor {
        span {
          color: ${getHoverFontColor};
        }
      }
    `}

  &:active {
    background-color: ${getActiveBackground};
    };

    outline: none;
    border-color: ${({ colorActive, theme, color }) => colorActive ?
    theme.colors[colorActive] :
    color
  };

    color: ${getPressedFontColor};


    &.hoverColor {
      span {
        color: ${getPressedFontColor};
      }
    }
  }

  @media screen and (max-width: 771px) {
    &.eye {
      width: 24px;
      height: 24px;
    }
  }
`;

const Button = ({
  children,
  leftIcon,
  rightIcon,
  style,
  tooltip,
  className,
  colorFont,
  colorBackground,
  colorHover,
  backgroundHover,
  colorActive,
  backgroundActive,
  colorFocus,
  forceHover,
  typographyType,
  color,
  withoutTypography,
  disabled,
  customBackground,
  type,
  rounded = false,
  customColor,
  ...props
}: IButtonProps) => {
  const tema = useThemeContext();
  const {
    enable = true,
    fullWidth,
    loading,
    onClick,
    size,
    colorType,
    variant,
    ...rest
  } = props;
  const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 24,
        color: tema.colors[getColor({ variant, colorType, enable })],
      }}
      spin
    />
  );
  return (
    <Tooltip title={tooltip} placement="bottom">
      <StyledButton
        {...rest}
        colorType={colorType}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        enable={enable}
        style={{ ...style }}
        onClick={disabled || !enable || loading ? () => { } : onClick}
        className={`hoverColor eventials-button ${className || ""}`}
        colorFont={colorFont}
        colorBackground={colorBackground}
        colorHover={colorHover}
        backgroundHover={backgroundHover}
        colorActive={colorActive}
        backgroundActive={backgroundActive}
        colorFocus={colorFocus}
        forceHover={forceHover}
        customBackground={customBackground}
        color={color}
        disabled={disabled}
        type={type}
        rounded={rounded}
      >
        {!loading && leftIcon && (
          <div aria-hidden="true" className="icon left-icon">
            {leftIcon}
          </div>
        )}
        {withoutTypography ? (
          children
        ) : (
          <Typography
            type={typographyType}
            color={getColor({
              variant,
              colorType,
              enable,
            })}
            customColor={customColor}
          >
            {loading && (
              <Spin
                wrapperClassName="loading"
                spinning={loading}
                indicator={antIcon}
                delay={500}
              />
            )}
            {children}
          </Typography>
        )}
        {!loading && rightIcon && (
          <div aria-hidden="true" className="icon right-icon">
            {rightIcon}
          </div>
        )}
      </StyledButton>
    </Tooltip>
  );
};

Button.defaultProps = {
  colorType: "primary",
  forceHover: false,
  typographyType: "button2",
};

export default Button;